/**
 * Question Management Service
 * 
 * This service layer provides an abstraction over question management API calls.
 * It handles question uploads, fetching, and filtering operations.
 * 
 * @module QuestionService
 */

import { 
  uploadCustomerQuestions, 
  getCustomerQuestionUploads, 
  getCustomerQuestionUploadById,
  getStandardQuestions 
} from '../utils/apiClient';

/**
 * Uploads a question set with file and/or typed questions
 * 
 * @param {FormData} formData - FormData containing branch, usecase, subusecase, questionSetName, and file
 * @returns {Promise<Object>} Upload response
 * 
 * @example
 * const formData = new FormData();
 * formData.append('branch', 'dental');
 * formData.append('usecase', 'prior-authorization');
 * formData.append('subusecase', 'verification_and_initiation');
 * formData.append('questionSetName', 'My Questions');
 * formData.append('file', fileObject);
 * const result = await QuestionService.uploadQuestions(formData);
 */
export const uploadQuestions = async (formData) => {
  try {
    const response = await uploadCustomerQuestions(formData);
    
    if (response.statusCode === 'SUCCESS') {
      return {
        success: true,
        message: response.message || 'Questions uploaded successfully',
        data: response.response
      };
    }
    
    return {
      success: false,
      message: response.message || 'Failed to upload questions'
    };
  } catch (error) {
    console.error('Error in QuestionService.uploadQuestions:', error);
    throw new Error(error.response?.data?.message || 'Failed to upload questions. Please try again.');
  }
};

/**
 * Fetches all question uploads with optional filters
 * 
 * @param {Object} params - Query parameters for filtering
 * @param {string} [params.branch] - Filter by branch
 * @param {string} [params.usecase] - Filter by usecase
 * @param {string} [params.subusecase] - Filter by subusecase
 * @param {string} [params.questionSetName] - Filter by question set name
 * @param {string} [params.status] - Filter by status (pending, processing, completed, failed)
 * @param {number} [params.skip] - Number of records to skip for pagination
 * @param {number} [params.limit] - Number of records to fetch
 * @returns {Promise<Object>} Question uploads response
 * 
 * @example
 * const uploads = await QuestionService.getAll({ branch: 'dental', status: 'pending' });
 */
export const getAll = async (params = {}) => {
  try {
    const response = await getCustomerQuestionUploads(params);
    
    if (response.statusCode === 'SUCCESS') {
      return {
        success: true,
        uploads: response.response.uploads || [],
        totalCount: response.response.total_count || 0,
        filteredCount: response.response.filtered_count || 0,
        message: response.message
      };
    }
    
    return {
      success: false,
      uploads: [],
      totalCount: 0,
      filteredCount: 0,
      message: response.message || 'Failed to fetch question uploads'
    };
  } catch (error) {
    console.error('Error in QuestionService.getAll:', error);
    throw new Error('Failed to fetch question uploads. Please try again.');
  }
};

/**
 * Gets a specific question upload by ID
 * 
 * @param {string} uploadId - The upload ID to fetch
 * @returns {Promise<Object>} Question upload details
 * 
 * @example
 * const upload = await QuestionService.getById('68ef3c3af8a8bd23f9a88fbd');
 */
export const getById = async (uploadId) => {
  try {
    const response = await getCustomerQuestionUploadById(uploadId);
    
    if (response.statusCode === 'SUCCESS') {
      return {
        success: true,
        data: response.response,
        message: response.message
      };
    }
    
    return {
      success: false,
      message: response.message || 'Failed to fetch question upload details'
    };
  } catch (error) {
    console.error('Error in QuestionService.getById:', error);
    throw new Error('Failed to fetch question upload details. Please try again.');
  }
};

/**
 * Gets standard questions for a specific path
 * 
 * @param {string} branch - Branch (e.g., 'medical')
 * @param {string} usecase - Usecase (e.g., 'benefits_verification')
 * @param {string} subusecase - Subusecase (e.g., 'default')
 * @returns {Promise<Object>} Standard questions response
 * 
 * @example
 * const questions = await QuestionService.getStandardQuestions('medical', 'benefits_verification', 'default');
 */
export const getStandardQuestionsForPath = async (branch, usecase, subusecase) => {
  try {
    // Construct the path in format: branch-usecase-subusecase
    const path = `${branch}-${usecase}-${subusecase}`;
    
    const response = await getStandardQuestions({
      path,
      type: 'standard'
    });
    
    if (response.statusCode === 'SUCCESS') {
      return {
        success: true,
        questions: response.response.questions || [],
        totalCount: response.response.total_count || 0,
        message: response.message
      };
    }
    
    return {
      success: false,
      questions: [],
      totalCount: 0,
      message: response.message || 'Failed to fetch standard questions'
    };
  } catch (error) {
    console.error('Error in QuestionService.getStandardQuestionsForPath:', error);
    throw new Error('Failed to fetch standard questions. Please try again.');
  }
};

/**
 * Validates question upload data before submission
 * 
 * @param {Object} uploadData - Upload data to validate
 * @returns {Object} Validation result
 * 
 * @example
 * const validation = QuestionService.validateUploadData(uploadData);
 * if (!validation.isValid) console.log(validation.errors);
 */
export const validateUploadData = (uploadData) => {
  const errors = [];
  
  if (!uploadData.questionSetName?.trim()) {
    errors.push('Question set name is required');
  }
  
  if (!uploadData.branch?.trim()) {
    errors.push('Branch is required');
  }
  
  if (!uploadData.usecase?.trim()) {
    errors.push('Usecase is required');
  }
  
  if (!uploadData.subusecase?.trim()) {
    errors.push('Sub usecase is required');
  }
  
  if (!uploadData.file && (!uploadData.typedQuestions || uploadData.typedQuestions.length === 0)) {
    errors.push('Either a file or typed questions must be provided');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

// Export as default object for easier importing
const QuestionService = {
  uploadQuestions,
  getAll,
  getById,
  getStandardQuestionsForPath,
  validateUploadData
};

export default QuestionService;
